import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await signIn(email, password);
      
      if (!data?.user) {
        throw new Error('Login failed. Please check your credentials.');
      }

      const user = data.user;

      // Get user profile to check role
      // First try to get from user metadata (set during signup)
      let userRole = user?.user_metadata?.role;
      
      // If not in metadata, query the database
      if (!userRole) {
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', user?.id)
          ?.single();

        if (profileError) {
          console.error('Profile error:', profileError);
          // If profile doesn't exist, check if we can create it or use metadata
          userRole = user?.user_metadata?.role;
          
          if (!userRole) {
            throw new Error('User profile not found. Please contact administrator.');
          }
        } else {
          userRole = profile?.role;
        }
      }

      // Check if user has admin role
      const adminRoles = ['super_admin', 'reviewer', 'comms_user', 'data_user'];
      if (!adminRoles.includes(userRole)) {
        await signOut();
        throw new Error('Access denied. Admin access required.');
      }

      // Store role in localStorage
      localStorage.setItem('userRole', userRole);
      localStorage.setItem('adminEmail', email);

      // Navigate based on role
      if (userRole === 'comms_user') {
        navigate('/admin-communication-center');
      } else {
        navigate('/admin-dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 relative overflow-hidden flex items-center justify-center p-4">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl p-8 lg:p-10 relative overflow-hidden">
          {/* Decorative gradient overlay */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl"></div>
          
          <div className="relative z-10">
            {/* Enhanced Logo and Header */}
            <div className="text-center mb-8">
              <div className="inline-flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl"></div>
                  <div className="relative w-20 h-20 lg:w-24 lg:h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-300 p-2">
                    <img 
                      src="/assets/images/MIJ-logo.png" 
                      alt="MI Junior Cricket Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent mb-2">
                Admin Login
              </h1>
              <p className="text-base text-gray-600 font-medium">
                Sign in to access the admin dashboard
              </p>
            </div>

            {/* Enhanced Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="AlertCircle" size={20} color="#FFFFFF" />
                  </div>
                  <p className="text-sm text-red-700 font-medium flex-1">{error}</p>
                </div>
              </div>
            )}

            {/* Enhanced Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="Mail" size={18} color="var(--color-muted-foreground)" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all shadow-sm hover:border-gray-300"
                    placeholder="admin@micricket.com"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all shadow-sm hover:border-gray-300"
                    placeholder="Enter your password"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading}
                iconName="LogIn"
                iconPosition="right"
                className="bg-gradient-to-r from-primary to-blue-600 hover:from-blue-700 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] mt-2"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Enhanced Back to Public Site */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <button
                onClick={() => navigate('/mobile-authentication')}
                className="text-sm text-gray-600 hover:text-primary font-medium transition-colors flex items-center justify-center gap-2 group"
              >
                <Icon name="ArrowLeft" size={16} color="currentColor" className="group-hover:-translate-x-1 transition-transform" />
                Back to Public Site
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secure admin access for MI Junior Cricket Tournament
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

